import React, { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid-pro'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'
import WrappedDataGrid from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid'
import { MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'
import {
  type CoinExposureData, type AccountExposureData,
  type PortfolioExposureData, type SymbolExposureData
} from '../../models/exposure/exposure-types'
import {
  createCoinExposureRows, extractForHeaderInitialExposure as extractInitialExposureByCoin, prefilterCoinExposureData,
} from '../../utils/view-specific/exposure/coin-exposure-utils'
import {
  createAccountExposureRows, extractForHeaderInitialExposure as extractInitialExposureByAccount
} from '../../utils/view-specific/exposure/account-exposure-utils'
import { createSymbolExposureRows } from '../../utils/view-specific/exposure/symbol-exposure-utils'
import { EXPOSURE_MODE_OPTIONS_CONFIG } from './exposure-data-grid-col-definitions'

export interface ExposureDataGridRow extends GridRowModelUpdate {
  id: string
  path: string[]
  coin: string
  account: string
  account_type: string
  symbol: string
  account_exposure: string
  price_usd: string
  exposure_coin: string
  exposure_usd: string
  initial_exposure_coin: string
  net_coin: string
  net_usd: string
}

interface ExposureDataGridProps {
  portfolio: string
}

const ExposureDataGrid: React.FC<ExposureDataGridProps> = ({ portfolio }) => {
  const [mode, setMode] = useState<MODE_OPTIONS>(MODE_OPTIONS.COIN)
  const [stableCoinVisibility, setStableCoinVisibility] = useState<boolean>(false)
  const toggleStableCoinVisibility = useCallback(() => {
    setStableCoinVisibility(!stableCoinVisibility)
  }, [stableCoinVisibility])

  const modeConfig = EXPOSURE_MODE_OPTIONS_CONFIG[mode]
  const modeEndpoint: string | undefined = modeConfig.endpoint
  const modeColumns: GridColDef[] = modeConfig.columns
  const modeAutosizeOptions = modeConfig.autosizeOptions
  const modeSortModel = modeConfig.sortModel

  const { data, error, mutate } = useSWR<PortfolioExposureData, boolean>(
    modeEndpoint,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000
    }
  )

  const [rows, setRows] = useState<ExposureDataGridRow[]>([])
  const [timestamp, setTimestamp] = useState<number>(0)
  const [totalCurrAsset, setTotalCurrAsset] = useState<string>('')
  const [pnlAsset, setPnLAsset] = useState<string>('')
  const [nextFund, setNextFund] = useState<string>('')
  const [initialExposureByCoin, setInitialExposureByCoin] = useState<Record<string, string>>({})
  const [sortModel, setSortModel] = React.useState<GridSortModel>(modeSortModel)

  useEffect(() => {
    if (error === true || data === undefined) {
      setRows([])
      setTotalCurrAsset('')
      setPnLAsset('')
      setNextFund('')
      setInitialExposureByCoin({})
    } else {
      const portfolioData = data[portfolio]
      switch (mode) {
        case 'coin':
          setRows(stableCoinVisibility
            ? createCoinExposureRows(portfolioData as CoinExposureData)
            : createCoinExposureRows(prefilterCoinExposureData(portfolioData as CoinExposureData))
          )
          setInitialExposureByCoin(extractInitialExposureByCoin(portfolioData as CoinExposureData))
          break
        case 'account':
          setRows(createAccountExposureRows(portfolioData as AccountExposureData))
          setInitialExposureByCoin(extractInitialExposureByAccount(portfolioData as AccountExposureData))
          break
        case 'symbol':
          setRows(createSymbolExposureRows(portfolioData as SymbolExposureData))
          setInitialExposureByCoin({})
          break
      }

      setTimestamp(data.timestamp)
      setTotalCurrAsset(portfolioData.total_curr_asset)
      setPnLAsset(portfolioData.pnl_asset)
      setNextFund(portfolioData.next_fund)

      setSortModel(modeSortModel)
    }
  }, [mode, stableCoinVisibility, data, error])

  return (
    <WrappedDataGrid
      gridData={{
        rows,
        columns: modeColumns,
        autosizeOptions: modeAutosizeOptions,
        sortModel,
        portfolio,
        csvOptions: { fileName: portfolio + '_exposure' }
      }}
      gridMode={{
        mode,
        modeOptions: Object.values(MODE_OPTIONS).filter(mode => mode === 'coin' || mode === 'account' || mode === 'symbol'),
        setMode,
      }}
      stableCoinVisibility={{
        stableCoinVisibility,
        toggleStableCoinVisibility
      }}
      toolbarData={{
        timestamp,
        pnlAsset,
        nextFund,
        totalCurrAsset,
        initialExposureByCoin,
      }}
      styleData={{
        background: 'rgba(70, 43, 0, 0.25)',
      }}
      mutate={mutate}
    />
  )
}

export default ExposureDataGrid
